import React, { useCallback, useEffect, useState } from 'react';
import { Plus, Pencil, Trash2 } from 'lucide-react';
import { PageHeader } from '../../components/shared/PageHeader';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
} from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '../../components/ui/dialog';
import { FormField } from '../../components/shared/FormField';
import { StatusToggle } from '../../components/shared/StatusToggle';
import ConfirmModal from '../../components/Common/ConfirmModal';
import { useAuth } from '../../hooks/useAuth';
import { useToast } from '../../context/ToastContext';
import cloudAssetService, {
  CloudProviderOption,
  CloudResourceType,
  CloudEnvironment,
  CloudTier,
} from '../../services/cloudAssetService';
import { userService } from '../../services/userService';
import { invalidateCloudProviders } from '../CloudAssets/useCloudProviders';
import CloudEnvironmentsTab from '../CloudAssets/CloudEnvironmentsTab';
import { LifecycleStatusMaster } from './MasterPages';

/**
 * Renders a type's `providerTypeNames` map as one compact string, e.g.
 * "AWS: EC2 · Azure: Virtual Machine" — a single column that stays a fixed
 * width no matter how many providers exist, rather than one table column per
 * provider (Story 92 made providers dynamic, so that count is unbounded).
 * Looks up each code against the provider list for its display name, falling
 * back to the raw code for one that's since been deleted.
 */
const formatProviderTypeNames = (
  map: Record<string, string> | undefined,
  providers: CloudProviderOption[],
): string => {
  if (!map || Object.keys(map).length === 0) return '—';
  const providerByCode = new Map(providers.map((p) => [p.code, p]));
  return Object.entries(map)
    .filter(([, name]) => name)
    .sort(([a], [b]) => {
      const sortA = providerByCode.get(a)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
      const sortB = providerByCode.get(b)?.sortOrder ?? Number.MAX_SAFE_INTEGER;
      return sortA - sortB || a.localeCompare(b);
    })
    .map(([code, name]) => `${providerByCode.get(code)?.name ?? code}: ${name}`)
    .join(' · ');
};

const EMPTY_PROVIDER = {
  code: '',
  name: '',
  sortOrder: 0,
  isActive: true,
};

const EMPTY_TIER = {
  label: '',
  value: '',
  description: '',
  sortOrder: 0,
  isActive: true,
};

const EMPTY = {
  category: '',
  normalizedType: '',
  providerTypeNames: {} as Record<string, string>,
  isActive: true,
};

/**
 * Cloud Setup (User Stories 82, 85, 94).
 *
 * Four sections. The original spec had seven — Discovery Settings has nothing
 * to configure without connections, and the tagging/ownership/cost-allocation
 * rule engines exist to enrich discovered data, which does not apply when a
 * person types the owner in directly. Providers came back as a real section in
 * User Story 92 once they stopped being a hardcoded enum, and Environments
 * joined them in User Story 94 — the tab is mirrored from the Cloud Assets page
 * rather than moved, because its resource-count and spend rollups are read by
 * Cloud Ops there, not just by admins here.
 */
export const CloudSetupPage: React.FC = () => {
  const { hasPermission } = useAuth();
  const { showToast } = useToast();
  const canManage = hasPermission('cloud.manage');

  const [providers, setProviders] = useState<CloudProviderOption[]>([]);
  const [types, setTypes] = useState<CloudResourceType[]>([]);
  const [loading, setLoading] = useState(true);

  // Environments are a cloud master like the three above (User Story 94), so
  // they belong on this page too. The tab component is reused verbatim from the
  // Cloud Assets page rather than reimplemented — one master, one implementation.
  // `users` feeds its Owner select.
  const [environments, setEnvironments] = useState<CloudEnvironment[]>([]);
  const [users, setUsers] = useState<any[]>([]);
  const [environmentsLoading, setEnvironmentsLoading] = useState(true);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<CloudResourceType | null>(null);
  const [form, setForm] = useState<Record<string, any>>(EMPTY);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [saving, setSaving] = useState(false);

  // Environment tier master (User Story 95). Unlike providers and types, a tier
  // may be deactivated while still referenced — retirement, not deletion — so
  // its Status column is an interactive StatusToggle rather than a badge.
  const [tiers, setTiers] = useState<CloudTier[]>([]);
  const [tierDialogOpen, setTierDialogOpen] = useState(false);
  const [editingTier, setEditingTier] = useState<CloudTier | null>(null);
  const [tierForm, setTierForm] = useState<Record<string, any>>(EMPTY_TIER);
  const [tierErrors, setTierErrors] = useState<Record<string, string>>({});
  const [savingTier, setSavingTier] = useState(false);
  const [pendingTierDelete, setPendingTierDelete] = useState<CloudTier | null>(null);
  const [tierTogglingId, setTierTogglingId] = useState<number | null>(null);

  const [providerDialogOpen, setProviderDialogOpen] = useState(false);
  const [editingProvider, setEditingProvider] =
    useState<CloudProviderOption | null>(null);
  const [providerForm, setProviderForm] =
    useState<Record<string, any>>(EMPTY_PROVIDER);
  const [providerErrors, setProviderErrors] = useState<Record<string, string>>({});
  const [savingProvider, setSavingProvider] = useState(false);
  const [pendingProviderDelete, setPendingProviderDelete] =
    useState<CloudProviderOption | null>(null);

  // allSettled, not all: these are independently-permissioned fetches and one
  // rejection must not blank the sections that did load. Lifecycle statuses are
  // not fetched here — the Lifecycle Statuses tab is now its own MasterManagement
  // instance (Story 85) and loads/refetches them itself.
  const load = useCallback(async () => {
    setLoading(true);
    const [providerResult, typeResult, tierResult] =
      await Promise.allSettled([
        cloudAssetService.getProviders(true),
        cloudAssetService.getResourceTypes(true),
        cloudAssetService.getTiers(true),
      ]);
    if (providerResult.status === 'fulfilled') setProviders(providerResult.value);
    if (typeResult.status === 'fulfilled') setTypes(typeResult.value);
    if (tierResult.status === 'fulfilled') setTiers(tierResult.value);
    if (tierResult.status === 'rejected') {
      showToast(
        (tierResult.reason as any)?.friendlyMessage ||
          'Failed to load environment tiers',
        'error',
      );
    }
    if (providerResult.status === 'rejected') {
      showToast(
        (providerResult.reason as any)?.friendlyMessage ||
          'Failed to load cloud providers',
        'error',
      );
    }
    if (typeResult.status === 'rejected') {
      showToast(
        (typeResult.reason as any)?.friendlyMessage ||
          'Failed to load resource types',
        'error',
      );
    }
    setLoading(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Kept separate from `load` so the Environments tab's `onChanged` refetches
   * only the environments, rather than every master on the page.
   *
   * The user list is deliberately NOT fetched here. It feeds the Owner select
   * and does not change while someone administers cloud masters, so refetching
   * it on every environment edit doubled this call for nothing — enough, with
   * StrictMode replaying effects in dev, to run into the API rate limit.
   */
  const loadEnvironments = useCallback(async () => {
    setEnvironmentsLoading(true);
    try {
      setEnvironments(await cloudAssetService.getEnvironments());
    } catch (error: any) {
      showToast(
        error?.friendlyMessage || 'Failed to load cloud environments',
        'error',
      );
    } finally {
      setEnvironmentsLoading(false);
    }
    // showToast comes from context and is stable.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * Tier-only refetch. Tier mutations used to call `load()`, which refetched
   * providers, resource types and lifecycle statuses too — three requests per
   * click that could not have changed.
   */
  const loadTiers = useCallback(async () => {
    try {
      setTiers(await cloudAssetService.getTiers(true));
    } catch (error: any) {
      showToast(
        error?.friendlyMessage || 'Failed to load environment tiers',
        'error',
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    load();
    loadEnvironments();
    // Users are fetched once for the Owner select; nothing on this page changes
    // them. A list the caller may not read only costs that select its options.
    userService
      .getUsers()
      .then((list) => setUsers(list as any))
      .catch(() => undefined);
  }, [load, loadEnvironments]);

  useEffect(() => {
    if (!tierDialogOpen) return;
    setTierErrors({});
    setTierForm(
      editingTier
        ? {
            label: editingTier.label,
            value: editingTier.value,
            description: editingTier.description ?? '',
            sortOrder: editingTier.sortOrder ?? 0,
            isActive: editingTier.isActive,
          }
        : EMPTY_TIER,
    );
  }, [tierDialogOpen, editingTier]);

  useEffect(() => {
    if (!dialogOpen) return;
    setErrors({});
    setForm(
      editing
        ? {
            category: editing.category,
            normalizedType: editing.normalizedType,
            providerTypeNames: { ...(editing.providerTypeNames ?? {}) },
            isActive: editing.isActive,
          }
        : EMPTY,
    );
  }, [dialogOpen, editing]);

  useEffect(() => {
    if (!providerDialogOpen) return;
    setProviderErrors({});
    setProviderForm(
      editingProvider
        ? {
            code: editingProvider.code,
            name: editingProvider.name,
            sortOrder: editingProvider.sortOrder,
            isActive: editingProvider.isActive,
          }
        : { ...EMPTY_PROVIDER, sortOrder: providers.length + 1 },
    );
  }, [providerDialogOpen, editingProvider, providers.length]);

  const set = (key: string, value: any) =>
    setForm((prev) => ({ ...prev, [key]: value }));

  // One input per active provider (Story 92 made providers dynamic — a fixed
  // AWS/Azure/GCP field set could never cover a provider added after release).
  const setProviderTypeName = (providerCode: string, value: string) =>
    setForm((prev: Record<string, any>) => ({
      ...prev,
      providerTypeNames: { ...prev.providerTypeNames, [providerCode]: value },
    }));

  const setProvider = (key: string, value: any) =>
    setProviderForm((prev) => ({ ...prev, [key]: value }));

  const handleProviderSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    const code = String(providerForm.code).trim().toLowerCase();
    if (!code) next.code = 'Code is required';
    else if (!/^[a-z0-9][a-z0-9._-]*$/.test(code)) {
      next.code = 'Use lowercase letters, digits, dot, dash or underscore';
    }
    if (!String(providerForm.name).trim()) next.name = 'Name is required';
    setProviderErrors(next);
    if (Object.keys(next).length) return;

    setSavingProvider(true);
    try {
      const payload = {
        ...providerForm,
        code,
        sortOrder: Number(providerForm.sortOrder) || 0,
      };
      if (editingProvider) {
        await cloudAssetService.updateProvider(editingProvider.id, payload);
        showToast('Cloud provider updated', 'success');
      } else {
        await cloudAssetService.createProvider(payload);
        showToast('Cloud provider created', 'success');
      }
      // The cloud screens cache this master, so a stale list would otherwise
      // survive until a full reload.
      invalidateCloudProviders();
      setProviderDialogOpen(false);
      load();
    } catch (error: any) {
      showToast(
        error?.friendlyMessage || 'Failed to save cloud provider',
        'error',
      );
    } finally {
      setSavingProvider(false);
    }
  };

  const handleProviderDelete = async () => {
    if (!pendingProviderDelete) return;
    try {
      await cloudAssetService.deleteProvider(pendingProviderDelete.id);
      showToast('Cloud provider deleted', 'success');
      invalidateCloudProviders();
      load();
    } catch (error: any) {
      // Refused while accounts or resources still reference it — that message
      // is the point of the guard, so it is shown verbatim.
      showToast(
        error?.friendlyMessage || 'Failed to delete cloud provider',
        'error',
      );
    } finally {
      setPendingProviderDelete(null);
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!String(form.category).trim()) next.category = 'Category is required';
    if (!String(form.normalizedType).trim()) {
      next.normalizedType = 'Normalized type is required';
    }
    setErrors(next);
    if (Object.keys(next).length) return;

    setSaving(true);
    try {
      if (editing) {
        await cloudAssetService.updateResourceType(editing.id, form);
        showToast('Resource type updated', 'success');
      } else {
        await cloudAssetService.createResourceType(form);
        showToast('Resource type created', 'success');
      }
      setDialogOpen(false);
      load();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to save resource type', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (type: CloudResourceType) => {
    if (!window.confirm(`Delete "${type.normalizedType}"?`)) return;
    try {
      await cloudAssetService.deleteResourceType(type.id);
      showToast('Resource type deleted', 'success');
      load();
    } catch (error: any) {
      // Refused while resources still classify by it — that message is the point.
      showToast(error?.friendlyMessage || 'Failed to delete resource type', 'error');
    }
  };

  const setTierField = (key: string, value: any) =>
    setTierForm((prev) => ({ ...prev, [key]: value }));

  const submitTier = async (event: React.FormEvent) => {
    event.preventDefault();
    const next: Record<string, string> = {};
    if (!String(tierForm.label).trim()) next.label = 'Label is required';
    setTierErrors(next);
    if (Object.keys(next).length) return;

    // Value defaults to the label on create; on edit it is sent only when the
    // admin actually changed it, so an unchanged tier never trips the
    // "already referenced" guard on the server.
    const payload: Record<string, any> = {
      label: String(tierForm.label).trim(),
      description: String(tierForm.description ?? '').trim() || undefined,
      sortOrder: Number(tierForm.sortOrder) || 0,
      isActive: Boolean(tierForm.isActive),
    };
    const typedValue = String(tierForm.value ?? '').trim();
    if (!editingTier) {
      payload.value = typedValue || String(tierForm.label).trim();
    } else if (typedValue && typedValue !== editingTier.value) {
      payload.value = typedValue;
    }

    setSavingTier(true);
    try {
      if (editingTier) {
        await cloudAssetService.updateTier(editingTier.id, payload);
        showToast('Environment tier updated', 'success');
      } else {
        await cloudAssetService.createTier(payload);
        showToast('Environment tier created', 'success');
      }
      setTierDialogOpen(false);
      loadTiers();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to save environment tier', 'error');
    } finally {
      setSavingTier(false);
    }
  };

  const toggleTier = async (tier: CloudTier, next: boolean) => {
    setTierTogglingId(tier.id);
    try {
      await cloudAssetService.updateTier(tier.id, { isActive: next });
      showToast(`${tier.label} ${next ? 'activated' : 'deactivated'}`, 'success');
      loadTiers();
    } catch (error: any) {
      showToast(error?.friendlyMessage || 'Failed to update tier status', 'error');
    } finally {
      setTierTogglingId(null);
    }
  };

  const confirmTierDelete = async () => {
    if (!pendingTierDelete) return;
    const tier = pendingTierDelete;
    setPendingTierDelete(null);
    try {
      await cloudAssetService.deleteTier(tier.id);
      showToast('Environment tier deleted', 'success');
      loadTiers();
    } catch (error: any) {
      // Refused while records still carry it — that message names the count,
      // which is the useful part.
      showToast(error?.friendlyMessage || 'Failed to delete environment tier', 'error');
    }
  };

  return (
    <div className="space-y-6">
      <PageHeader
        title="Cloud Setup"
        description="Providers, environments, resource type catalog and lifecycle statuses for the cloud register."
      />

      <Tabs defaultValue="providers">
        <TabsList>
          <TabsTrigger value="providers">Providers</TabsTrigger>
          <TabsTrigger value="environments">Environments</TabsTrigger>
          <TabsTrigger value="tiers">Tiers</TabsTrigger>
          <TabsTrigger value="types">Resource Types</TabsTrigger>
          <TabsTrigger value="lifecycle">Lifecycle Statuses</TabsTrigger>
        </TabsList>

        <TabsContent value="environments" className="space-y-4 pt-4">
          <div className="overflow-hidden rounded-md border">
            <CloudEnvironmentsTab
              environments={environments}
              tiers={tiers}
              users={users}
              loading={environmentsLoading}
              onChanged={loadEnvironments}
              variant="master"
            />
          </div>
        </TabsContent>

        <TabsContent value="tiers" className="space-y-4 pt-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              The prod / non-prod tiers resources and environments are tagged
              with. The value is what gets stored on each record, so it cannot be
              changed once anything references it — rename the label instead.
              Deactivating a tier hides it from the dropdowns without touching
              the records already tagged with it.
            </p>
            {canManage && (
              <Button
                className="shrink-0"
                onClick={() => {
                  setEditingTier(null);
                  setTierDialogOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Tier
              </Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left font-medium">Label</th>
                  <th className="p-3 text-left font-medium">Stored Value</th>
                  <th className="p-3 text-left font-medium">Description</th>
                  <th className="p-3 text-left font-medium">Order</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  {canManage && (
                    <th className="p-3 text-right font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : tiers.length === 0 ? (
                  <tr>
                    <td colSpan={6} className="p-6 text-center text-muted-foreground">
                      No environment tiers configured. Add one before tagging
                      resources or environments.
                    </td>
                  </tr>
                ) : (
                  tiers.map((tier) => (
                    <tr key={tier.id} className="border-t">
                      <td className="p-3 font-medium">{tier.label}</td>
                      <td className="p-3 font-mono text-xs">{tier.value}</td>
                      <td className="p-3 text-muted-foreground">
                        {tier.description || '—'}
                      </td>
                      <td className="p-3">{tier.sortOrder}</td>
                      <td className="p-3">
                        <StatusToggle
                          checked={tier.isActive}
                          onToggle={
                            canManage ? (next) => toggleTier(tier, next) : undefined
                          }
                          loading={tierTogglingId === tier.id}
                          ariaLabel={`Toggle status for ${tier.label}`}
                        />
                      </td>
                      {canManage && (
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingTier(tier);
                                setTierDialogOpen(true);
                              }}
                              aria-label={`Edit ${tier.label}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPendingTierDelete(tier)}
                              aria-label={`Delete ${tier.label}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="providers" className="space-y-4 pt-4">
          <div className="flex items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground">
              The providers cloud accounts and resources can be recorded against.
              The code is what gets stored on each record, so it cannot be changed
              once anything references it — rename the display name instead.
            </p>
            {canManage && (
              <Button
                onClick={() => {
                  setEditingProvider(null);
                  setProviderDialogOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Provider
              </Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left font-medium">Name</th>
                  <th className="p-3 text-left font-medium">Code</th>
                  <th className="p-3 text-left font-medium">Order</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  {canManage && (
                    <th className="p-3 text-right font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : providers.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-muted-foreground">
                      No cloud providers configured. Add one before recording
                      accounts or resources.
                    </td>
                  </tr>
                ) : (
                  providers.map((provider) => (
                    <tr key={provider.id} className="border-t">
                      <td className="p-3 font-medium">{provider.name}</td>
                      <td className="p-3 font-mono text-xs">{provider.code}</td>
                      <td className="p-3">{provider.sortOrder}</td>
                      <td className="p-3">
                        <Badge variant={provider.isActive ? 'secondary' : 'outline'}>
                          {provider.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      {canManage && (
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditingProvider(provider);
                                setProviderDialogOpen(true);
                              }}
                              aria-label={`Edit ${provider.name}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPendingProviderDelete(provider)}
                              aria-label={`Delete ${provider.name}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="types" className="space-y-4 pt-4">
          <div className="flex items-center justify-between">
            <p className="text-sm text-muted-foreground">
              The normalized type is what users see and what the import resolves
              against; the provider columns let a console export match without
              retyping.
            </p>
            {canManage && (
              <Button
                onClick={() => {
                  setEditing(null);
                  setDialogOpen(true);
                }}
              >
                <Plus className="mr-2 h-4 w-4" />
                Add Type
              </Button>
            )}
          </div>

          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr>
                  <th className="p-3 text-left font-medium">Category</th>
                  <th className="p-3 text-left font-medium">Normalized Type</th>
                  <th className="p-3 text-left font-medium">Provider Names</th>
                  <th className="p-3 text-left font-medium">Status</th>
                  {canManage && (
                    <th className="p-3 text-right font-medium">Actions</th>
                  )}
                </tr>
              </thead>
              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-muted-foreground">
                      Loading…
                    </td>
                  </tr>
                ) : types.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="p-6 text-center text-muted-foreground">
                      No resource types configured.
                    </td>
                  </tr>
                ) : (
                  types.map((type) => (
                    <tr key={type.id} className="border-t">
                      <td className="p-3">{type.category}</td>
                      <td className="p-3 font-medium">{type.normalizedType}</td>
                      <td className="p-3 text-muted-foreground">
                        {formatProviderTypeNames(type.providerTypeNames, providers)}
                      </td>
                      <td className="p-3">
                        <Badge variant={type.isActive ? 'secondary' : 'outline'}>
                          {type.isActive ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      {canManage && (
                        <td className="p-3 text-right">
                          <div className="flex justify-end gap-1">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setEditing(type);
                                setDialogOpen(true);
                              }}
                              aria-label={`Edit ${type.normalizedType}`}
                            >
                              <Pencil className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDelete(type)}
                              aria-label={`Delete ${type.normalizedType}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </td>
                      )}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="lifecycle" className="space-y-4 pt-4">
          <p className="text-sm text-muted-foreground">
            The ITAM decision about a resource, kept separate from the provider's
            running/stopped state.
          </p>
          <div className="overflow-hidden rounded-md border">
            <LifecycleStatusMaster />
          </div>
        </TabsContent>
      </Tabs>

      <Dialog open={dialogOpen} onOpenChange={(next) => !next && setDialogOpen(false)}>
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editing ? 'Edit Resource Type' : 'Add Resource Type'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <FormField
              label="Category"
              required
              error={errors.category}
              hint="Broad grouping used in filters, e.g. Compute"
              id="type-category"
            >
              <Input
                id="type-category"
                value={form.category}
                onChange={(e) => set('category', e.target.value)}
              />
            </FormField>
            <FormField
              label="Normalized Type"
              required
              error={errors.normalizedType}
              id="type-normalized"
            >
              <Input
                id="type-normalized"
                value={form.normalizedType}
                onChange={(e) => set('normalizedType', e.target.value)}
              />
            </FormField>
            {providers.filter((p) => p.isActive).length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No active providers yet — add one on the Providers tab to record
                its native name for this type.
              </p>
            ) : (
              providers
                .filter((p) => p.isActive)
                .map((p) => (
                  <FormField key={p.code} label={`${p.name} Name`} id={`type-provider-${p.code}`}>
                    <Input
                      id={`type-provider-${p.code}`}
                      value={form.providerTypeNames?.[p.code] ?? ''}
                      onChange={(e) => setProviderTypeName(p.code, e.target.value)}
                      placeholder="e.g. Compute Engine"
                    />
                  </FormField>
                ))
            )}
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(form.isActive)}
                onChange={(e) => set('isActive', e.target.checked)}
              />
              Active
            </label>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving…' : editing ? 'Save Changes' : 'Add Type'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
      <Dialog
        open={providerDialogOpen}
        onOpenChange={(next) => !next && setProviderDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingProvider ? 'Edit Provider' : 'Add Provider'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleProviderSubmit} className="space-y-4">
            <FormField
              label="Display Name"
              required
              error={providerErrors.name}
              hint="What users see in filters and forms, e.g. Oracle Cloud"
              id="provider-name"
            >
              <Input
                id="provider-name"
                value={providerForm.name}
                onChange={(e) => setProvider('name', e.target.value)}
              />
            </FormField>
            <FormField
              label="Code"
              required
              error={providerErrors.code}
              hint={
                editingProvider
                  ? 'Stored on every account and resource — locked once referenced'
                  : 'Lowercase identifier stored on each record, e.g. oracle'
              }
              id="provider-code"
            >
              <Input
                id="provider-code"
                value={providerForm.code}
                onChange={(e) => setProvider('code', e.target.value)}
                placeholder="oracle"
              />
            </FormField>
            <FormField label="Sort Order" id="provider-sort">
              <Input
                id="provider-sort"
                type="number"
                value={providerForm.sortOrder}
                onChange={(e) => setProvider('sortOrder', e.target.value)}
              />
            </FormField>
            <label className="flex items-center gap-2 text-sm">
              <input
                type="checkbox"
                checked={Boolean(providerForm.isActive)}
                onChange={(e) => setProvider('isActive', e.target.checked)}
              />
              Active
            </label>
            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => setProviderDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={savingProvider}>
                {savingProvider
                  ? 'Saving…'
                  : editingProvider
                    ? 'Save Changes'
                    : 'Add Provider'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        show={Boolean(pendingProviderDelete)}
        title="Delete Cloud Provider"
        message={`Delete "${pendingProviderDelete?.name ?? ''}"? This cannot be undone.`}
        confirmText="Delete"
        type="danger"
        onConfirm={handleProviderDelete}
        onCancel={() => setPendingProviderDelete(null)}
      />

      <Dialog
        open={tierDialogOpen}
        onOpenChange={(next) => !next && setTierDialogOpen(false)}
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingTier ? 'Edit Environment Tier' : 'Add Environment Tier'}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={submitTier} className="space-y-4">
            <FormField label="Label" required error={tierErrors.label} id="tier-label">
              <Input
                id="tier-label"
                value={tierForm.label}
                onChange={(e) => setTierField('label', e.target.value)}
                placeholder="Production"
              />
            </FormField>

            <FormField label="Stored Value" id="tier-value">
              <Input
                id="tier-value"
                value={tierForm.value}
                onChange={(e) => setTierField('value', e.target.value)}
                placeholder="Defaults to the label"
                disabled={Boolean(editingTier)}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                {editingTier
                  ? 'Records store this value, so it cannot be edited here. Rename the label instead.'
                  : 'What gets stored on each record. Leave blank to use the label.'}
              </p>
            </FormField>

            <FormField label="Description" id="tier-description">
              <Input
                id="tier-description"
                value={tierForm.description}
                onChange={(e) => setTierField('description', e.target.value)}
                placeholder="Live, customer-facing workloads"
              />
            </FormField>

            <FormField label="Sort Order" id="tier-sort">
              <Input
                id="tier-sort"
                type="number"
                value={tierForm.sortOrder}
                onChange={(e) => setTierField('sortOrder', e.target.value)}
              />
            </FormField>

            <DialogFooter>
              <Button
                type="button"
                variant="ghost"
                onClick={() => setTierDialogOpen(false)}
              >
                Cancel
              </Button>
              <Button type="submit" disabled={savingTier}>
                {savingTier ? 'Saving…' : editingTier ? 'Save Changes' : 'Add Tier'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <ConfirmModal
        show={Boolean(pendingTierDelete)}
        title="Delete Environment Tier"
        message={`Delete "${pendingTierDelete?.label ?? ''}"? This cannot be undone. If any record still uses it, deactivate it instead.`}
        confirmText="Delete"
        type="danger"
        onConfirm={confirmTierDelete}
        onCancel={() => setPendingTierDelete(null)}
      />
    </div>
  );
};

export default CloudSetupPage;
